import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ConfirmationMaterialConsumeModel,
    EventService,
    IMaterialPickupResult,
    MATERIAL_STATUS_CODE,
    MaterialModel,
    MaterialService,
    ModalClass,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmationAddMaterialComponent } from './confirmation-add-material/confirmation-add-material.component';
import ConfirmationAddMaterialModel from './confirmation-add-material/confirmation-add-material.model';
import { ConfirmationMaterialConsumeUtilitiesService } from './confirmation-material-consume-utilities.service';
import { MaterialStockService } from '../../material/services/material-stock-service';

@Component({
    selector: 'pace-confirmation-material-consume',
    templateUrl: './confirmation-material-consume.component.html',
    styleUrls: ['./confirmation-material-consume.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationMaterialConsumeComponent implements OnInit, OnDestroy, DoCheck {
    @Input()
    public model: ConfirmationMaterialConsumeModel;

    @Input()
    public parentForm: FormArray;

    public readonly displayAddMaterialButton: boolean;
    public isLoadingBootStockData: boolean;
    public isInvitro: boolean;

    public confirmationMaterialForm: FormGroup;
    public confirmationMaterialFormChildren: FormArray;

    private destroy$: Subject<void>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly eventService: EventService,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly materialService: MaterialService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly confirmationMaterialConsumeUtilities: ConfirmationMaterialConsumeUtilitiesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly configService: ConfigService,
        private readonly materialStockService: MaterialStockService,
    ) {
        this.displayAddMaterialButton = this.configService.getDisplayAddMaterialButton();
    }

    public ngOnInit(): void {
        const confirmationMaterialValidator = (): ValidationErrors | null => this.checkValidity();
        this.confirmationMaterialFormChildren = this.formBuilder.array([]);

        this.confirmationMaterialForm = this.formBuilder.group(
            {
                formChildren: this.confirmationMaterialFormChildren,
            },
            {
                validators: confirmationMaterialValidator,
            },
        );
        this.parentForm.push(this.confirmationMaterialForm);
        this.destroy$ = new Subject<void>();

        this.eventService.triggerMaterialPicked$
            .pipe(
                tap(() => {
                    this.model.listMaterial.value = this.materialService.getAllMaterialsForNotification(
                        this.model.notification.identifier,
                    );
                    this.eventService.triggerMaterialBillingIndicatorsNotReceived(this.model.listMaterial.value);
                    this.changeDetector.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.isInvitro = this.model.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
    }

    public ngDoCheck(): void {
        this.confirmationMaterialFormChildren.controls.forEach((control) => control.updateValueAndValidity());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public openBootStock(): void {
        this.isLoadingBootStockData = true;
        this.materialStockService
            .triggerBootStock(this.model.notification, this.model.listMaterial)
            .pipe(
                tap(() => {
                    this.isLoadingBootStockData = false;
                    this.changeDetector.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public openOtherStock(): void {
        this.materialStockService.triggerOtherStock(this.model.notification, this.model.listMaterial);
    }

    public openAddMaterialUI(): void {
        const modalInstance = this.modalService.show(ConfirmationAddMaterialComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new ConfirmationAddMaterialModel(this.model.notification),
            },
            ariaLabelledBy: 'confirmation-add-material-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                mergeMap((result: IMaterialPickupResult) => {
                    if (result && result.materialModel) {
                        result.materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.PICKED;
                        result.materialModel.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_PICKEDUP';
                        result.materialModel.maxQuantity = result.materialModel.quantity.value ?? 0;
                        result.materialModel.quantity = assignModelOptions(
                            result.materialModel.quantity,
                            {
                                ...result.materialModel.quantity.options,
                                minValue: 1,
                                maxValue: result.materialModel.quantity.value,
                            },
                            result.materialModel,
                        );
                        this.model.listMaterial.addModel(result.materialModel);
                        return this.materialService.addMaterialToLocalStorage(result.materialModel);
                    }
                    return of(undefined);
                }),
                catchError((error) => {
                    this.log.error(
                        'Error in ConfirmationMaterialConsumeComponent -> openAddMaterialUI -> materialService.addMaterialToLocalStorage: ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => this.changeDetector.detectChanges());
    }

    /**
     * Set the form validity as required on the material consumption form
     * if the option 'N/A' is selected
     *
     * @returns {null | ValidationErrors} - Returns null if no errors, otherwise returns an object with errors
     */
    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        if (this.clientManager.isNgmP58()) {
            const isNASelected = this.model.listMaterial.value.some((material) => {
                return (
                    this.confirmationMaterialConsumeUtilities.showComplaintPart(material, this.model) &&
                    material.isConsumed &&
                    material.isComplaint.selectedEntry.value === ConfigConstants.optionNA
                );
            });

            if (isNASelected) {
                errors.required = `Please mark if this material is a complained part`;
            }
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public updateListMaterial(materials: MaterialModel[]): void {
        this.model.listMaterial.value = materials;
    }

    public onMaterialRemoved(removed: boolean): void {
        if (removed) {
            this.changeDetector.detectChanges();
        }
    }
}
